import { Component, inject, input, output, signal } from '@angular/core';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { NotificationService } from '@/app/services/notification.service';
import {
    BrandingAssetRole,
    SharedContentService,
} from '@/app/services/shared-content.service';

/**
 * "Upload" trigger for a branding asset field: opens the file picker,
 * validates size client-side, uploads into the shared content store under
 * `branding/{realmId}/{role}.{ext}` and emits the resulting relative URL.
 * The backend restricts the upload to global admins — pass `disabled` +
 * `disabledReason` for scoped admins instead of letting the call fail.
 */
@Component({
    selector: 'app-asset-upload',
    standalone: true,
    imports: [Button, Tooltip],
    templateUrl: './asset-upload.html',
})
export class AssetUpload {
    private readonly sharedContent = inject(SharedContentService);
    private readonly notification = inject(NotificationService);

    readonly realmId = input.required<string>();
    readonly role = input.required<BrandingAssetRole>();
    /** `accept` attribute of the file input (image MIME types / extensions). */
    readonly accept = input<string>('image/*');
    readonly maxBytes = input<number>(5 * 1024 * 1024);

    readonly disabled = input<boolean>(false);
    readonly disabledReason = input<string | null>(null);

    /** The uploaded file's relative URL in the shared content store. */
    readonly uploaded = output<string>();

    protected readonly busy = signal(false);

    protected async onFilePicked(eventTarget: EventTarget | null): Promise<void> {
        const fileInput = eventTarget as HTMLInputElement;
        const file = fileInput.files?.[0];
        // Allow re-picking the same file after a failed attempt.
        fileInput.value = '';
        if (!file || this.busy()) return;

        if (file.size > this.maxBytes()) {
            const limitMb = (this.maxBytes() / (1024 * 1024)).toFixed(1);
            this.notification.show({
                severity: 'warn',
                summary: 'File too large',
                detail: `"${file.name}" exceeds the ${limitMb} MB limit for this asset.`,
            });
            return;
        }

        this.busy.set(true);
        try {
            const url = await this.sharedContent.uploadBrandingFile(
                this.realmId(),
                this.role(),
                file,
            );
            if (url) this.uploaded.emit(url);
        } finally {
            this.busy.set(false);
        }
    }
}
